/**
 * Get the display name for a field
 * 
 * Checks the translations array for en-US translation first,
 * then falls back to converting snake_case to Title Case.
 */

interface FieldTranslation {
    language: string;
    translation: string;
}

interface FieldMeta {
    translations?: FieldTranslation[] | null;
}

interface Field {
    field: string;
    meta?: FieldMeta | null;
}

/**
 * Format a field name from snake_case to Title Case
 */
export function formatTitle(field: string): string {
    return field
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Get the display name for a field
 * 
 * Priority:
 * 1. Translation for en-US from meta.translations
 * 2. Fallback to formatted field name (snake_case -> Title Case)
 * 
 * @param field - Field object with field name and meta
 * @returns Display name string
 */
export function getFieldDisplayName(field: Field | null | undefined): string {
    if (!field) return '';

    // Check for en-US translation
    if (field.meta?.translations && Array.isArray(field.meta.translations)) {
        const enUSTranslation = field.meta.translations.find(
            (t) => t.language === 'en-US'
        );
        if (enUSTranslation?.translation) {
            return enUSTranslation.translation;
        }
    }

    // Fallback to formatted field name
    return formatTitle(field.field);
}

/**
 * Get translation value for a specific language
 * 
 * @param translations - Array of translations
 * @param language - Language code (defaults to 'en-US')
 * @returns Translation string or empty string if not found
 */
export function getTranslation(
    translations: FieldTranslation[] | null | undefined,
    language: string = 'en-US'
): string {
    if (!translations || !Array.isArray(translations)) return '';
    
    const translation = translations.find((t) => t.language === language);
    return translation?.translation || '';
}

/**
 * Set or update a translation for a specific language
 * 
 * @param translations - Current translations array
 * @param language - Language code
 * @param value - Translation value
 * @returns New translations array
 */
export function setTranslation(
    translations: FieldTranslation[] | null | undefined,
    language: string,
    value: string
): FieldTranslation[] {
    const currentTranslations = translations ? [...translations] : [];
    
    const existingIndex = currentTranslations.findIndex((t) => t.language === language);
    
    if (value.trim() === '') {
        // Remove translation if empty
        if (existingIndex >= 0) {
            currentTranslations.splice(existingIndex, 1);
        }
    } else if (existingIndex >= 0) {
        // Update existing translation
        currentTranslations[existingIndex] = { language, translation: value };
    } else {
        // Add new translation
        currentTranslations.push({ language, translation: value });
    }
    
    return currentTranslations;
}



